/**
 * App Handoff - Product Page (Phase 2/3)
 * Applies handoff options, injects _appHandoffId, and auto-starts Design in Browser.
 *
 * Flow:
 *   1. Check for ?app_handoff= URL param.
 *   2. Read/validate localStorage context.
 *   3. Show informational banner.
 *   4. Apply selected options to product page dropdowns.
 *   5. Inject _appHandoffId into variantSelects.properties.
 *   6. Auto-start Design in Browser.
 */
(function () {
  'use strict';

  var params = new URLSearchParams(window.location.search);
  var handoffId = params.get('app_handoff');

  if (!handoffId) return;

  // ─── Prevent re-run on back navigation ────────────────────────────────────
  var startedKey = 'appHandoffStarted_' + handoffId;
  var alreadyStarted = false;
  try {
    alreadyStarted = localStorage.getItem(startedKey) === 'true';
  } catch (e) {}

  // ─── Read and Validate Context ────────────────────────────────────────────

  var context = null;
  try {
    var raw = localStorage.getItem('appHandoff_' + handoffId);
    if (raw) {
      context = JSON.parse(raw);
    }
  } catch (e) {}

  var currentHandle = window.arkaProductHandle || null;
  var isValid = false;

  if (context) {
    var now = Date.now();
    var notExpired = !context.expiresAt || now < context.expiresAt;
    var handleMatches = !currentHandle || context.productHandle === currentHandle;
    isValid = notExpired && handleMatches;
  }

  // ─── Render Banner ────────────────────────────────────────────────────────

  var container = document.getElementById('app-handoff-product-banner');
  if (!container) {
    var anchor = document.querySelector('.customize') || document.querySelector('product-form');
    if (!anchor) return;
    container = document.createElement('div');
    container.id = 'app-handoff-product-banner';
    anchor.parentNode.insertBefore(container, anchor);
  }

  if (!isValid) {
    container.innerHTML =
      '<div class="app-handoff-banner app-handoff-banner--expired">' +
        '<p class="app-handoff-banner__title">Handoff session expired</p>' +
        '<p class="app-handoff-banner__body">Your Arka app handoff session has expired or could not be loaded. ' +
        'Please select your options on this page or return to the Arka app.</p>' +
      '</div>';
    return;
  }

  // Build options list HTML
  var optionsHtml = '';
  var opts = context.selectedOptions || {};
  var displayKeys = ['size', 'material', 'printing', 'production'];

  for (var i = 0; i < displayKeys.length; i++) {
    var key = displayKeys[i];
    var val = opts[key] || opts[key.charAt(0).toUpperCase() + key.slice(1)];
    if (val) {
      optionsHtml += '<li><strong>' + capitalize(key) + ':</strong> ' + escapeHtml(String(val)) + '</li>';
    }
  }

  if (context.quantity) {
    optionsHtml += '<li><strong>Quantity:</strong> ' + escapeHtml(String(context.quantity)) + '</li>';
  }

  if (opts.package || opts.Package) {
    optionsHtml += '<li><strong>Package:</strong> ' + escapeHtml(String(opts.package || opts.Package)) + '</li>';
  }

  container.innerHTML =
    '<div class="app-handoff-banner app-handoff-banner--active">' +
      '<p class="app-handoff-banner__title">Options loaded from the Arka app</p>' +
      '<p class="app-handoff-banner__body">Applying your selections and launching the design tool...</p>' +
      (optionsHtml ? '<ul class="app-handoff-banner__options">' + optionsHtml + '</ul>' : '') +
    '</div>';

  // If already started once (back-button scenario), show banner but don't re-trigger
  if (alreadyStarted) {
    container.querySelector('.app-handoff-banner__body').textContent =
      'Your design session was already started. Click Design in Browser if you need to restart.';
    return;
  }

  // ─── Apply Options & Auto-Start ───────────────────────────────────────────

  // Wait for variant-selects custom element to be defined and ready
  waitForVariantSelects(function (variantSelects) {
    console.log('[app-handoff-product] variantSelects ready, applying options...');
    applyHandoffOptions(variantSelects, context, function () {
      injectAppHandoffId(variantSelects, context);
      autoStartDesignInBrowser(context);
    });
  });

  // ─── Option Application ───────────────────────────────────────────────────

  function waitForVariantSelects(callback) {
    // variant-selects may already exist or may be defined shortly
    var el = document.querySelector('variant-selects');
    if (el) {
      // Small delay to let constructor finish (dropdowns bind change listeners)
      setTimeout(function () { callback(el); }, 200);
      return;
    }

    // Wait for custom element to be defined
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('variant-selects').then(function () {
        var el2 = document.querySelector('variant-selects');
        if (el2) {
          setTimeout(function () { callback(el2); }, 200);
        } else {
          showFallback('Could not find product options on this page.');
        }
      });
    } else {
      showFallback('Could not find product options on this page.');
    }
  }

  function applyHandoffOptions(variantSelects, ctx, onComplete) {
    var selectedOpts = ctx.selectedOptions || {};
    var dropdowns = variantSelects.querySelectorAll('.custom-dropdown-component');

    console.log('[app-handoff-product] selectedOptions:', JSON.stringify(selectedOpts));
    console.log('[app-handoff-product] found dropdowns:', dropdowns.length);

    // Order matters: material first (it may filter sizes), then size, then others
    var applyOrder = ['material', 'size', 'printing', 'production', 'qty', 'package'];
    var dropdownMap = {};

    dropdowns.forEach(function (dd) {
      var name = (dd.dataset.name || '').toLowerCase();
      dropdownMap[name] = dd;
    });

    console.log('[app-handoff-product] dropdown names:', Object.keys(dropdownMap).join(', '));

    // Apply options sequentially with small delays to let change events cascade
    var idx = 0;
    function applyNext() {
      if (idx >= applyOrder.length) {
        // All standard options applied, now handle quantity
        applyQuantity(variantSelects, ctx, dropdownMap, function () {
          // Final settle time for onVariantChange async operations
          setTimeout(onComplete, 600);
        });
        return;
      }

      var optName = applyOrder[idx];
      idx++;

      // Find value from context
      var optValue = findOptionValue(selectedOpts, optName, ctx);

      if (!optValue || !dropdownMap[optName]) {
        console.log('[app-handoff-product] skip', optName, '- no value or no dropdown');
        applyNext();
        return;
      }

      var dd = dropdownMap[optName];

      // For size: if the value is a custom 3D dimension (LxWxH), use the custom-size tab
      if (optName === 'size' && isCustom3dSize(optValue)) {
        var applied = applyCustomSize(dd, optValue);
        console.log('[app-handoff-product] apply custom size =', optValue, '->', applied ? 'OK' : 'FAILED');
        setTimeout(applyNext, 300);
        return;
      }

      var applied = selectDropdownValue(dd, optValue);
      console.log('[app-handoff-product] apply', optName, '=', optValue, '->', applied ? 'OK' : 'NOT FOUND');

      // Delay to let change cascade
      setTimeout(applyNext, 150);
    }

    applyNext();
  }

  /**
   * Checks if a size string is a custom 3D dimension (LxWxH).
   */
  function isCustom3dSize(size) {
    if (!size) return false;
    var parts = size.replace(/\s/g, '').split('x');
    return parts.length >= 3 && parts.every(function (p) { return !isNaN(parseFloat(p)); });
  }

  /**
   * Applies a custom 3D size via the custom-size tab and form inputs.
   * This handles the case where a size like "7x5x3" is not a pre-defined dropdown option.
   */
  function applyCustomSize(sizeDropdown, sizeValue) {
    var parts = sizeValue.replace(/\s/g, '').split('x');
    if (parts.length < 3) return false;

    var length = parts[0];
    var width = parts[1];
    var height = parts[2];

    // Activate the custom size tab
    var customTab = document.querySelector('.content-tab-size[data-type="custom"]');
    if (customTab && !customTab.classList.contains('active')) {
      customTab.click();
    }

    // Fill in the custom size form inputs
    var form = sizeDropdown.querySelector('form.custom-size');
    if (!form) {
      // Fallback: directly set dataset values on the dropdown
      sizeDropdown.dataset.value = 'custom';
      sizeDropdown.dataset.customValue = length + 'x' + width + 'x' + height;
      sizeDropdown.dispatchEvent(new Event('change', { bubbles: true }));
      console.log('[app-handoff-product] custom size applied via dataset fallback:', sizeValue);
      return true;
    }

    var lengthInput = form.querySelector('input[name="length"]');
    var widthInput = form.querySelector('input[name="width"]');
    var heightInput = form.querySelector('input[name="height"]');

    if (lengthInput) {
      lengthInput.value = length;
      lengthInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
    if (widthInput) {
      widthInput.value = width;
      widthInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
    if (heightInput) {
      heightInput.value = height;
      heightInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Trigger save on the size dropdown (mimics user clicking Save)
    var saveBtn = sizeDropdown.querySelector('.save-custom-size');
    if (saveBtn) {
      setTimeout(function () {
        saveBtn.click();
      }, 50);
    } else {
      // Manual save: set dataset values and dispatch change
      sizeDropdown.dataset.value = 'custom';
      sizeDropdown.dataset.customValue = length + 'x' + width + 'x' + height;
      sizeDropdown.dispatchEvent(new Event('change', { bubbles: true }));
    }

    console.log('[app-handoff-product] custom size applied via form:', sizeValue);
    return true;
  }

  function findOptionValue(selectedOpts, optName, ctx) {
    // Normalize: try lowercase key first, then capitalized
    var val = selectedOpts[optName] || selectedOpts[capitalize(optName)];

    // Special: qty comes from context.quantity if not in selectedOptions
    if (!val && optName === 'qty') {
      val = ctx.quantity ? String(ctx.quantity) : null;
    }

    // Special: package from selectedOptions
    if (!val && optName === 'package') {
      val = selectedOpts['package'] || selectedOpts['Package'];
    }

    return val ? String(val) : null;
  }

  function selectDropdownValue(dropdown, targetValue) {
    // Get all option elements in this dropdown
    var options = dropdown.querySelectorAll('[role="option"], .selectCustom-option');
    if (!options || options.length === 0) return false;

    var normalizedTarget = normalizeForComparison(targetValue);

    var matchedOption = null;
    for (var i = 0; i < options.length; i++) {
      var opt = options[i];
      if (opt.hasAttribute('aria-hidden')) continue;

      var optVal = opt.dataset.value || '';
      if (normalizeForComparison(optVal) === normalizedTarget) {
        matchedOption = opt;
        break;
      }
    }

    if (!matchedOption) return false;

    // Use the custom element's setter which handles UI update + event dispatch
    dropdown.currentOption = matchedOption;
    return true;
  }

  function applyQuantity(variantSelects, ctx, dropdownMap, onDone) {
    var quantity = ctx.quantity;
    if (!quantity) {
      onDone();
      return;
    }

    // If product uses package dropdown, quantity was already handled via applyOrder
    // For quantity-dropdown with editable input, set the value directly
    var qtyDropdown = dropdownMap['qty'];
    if (qtyDropdown && qtyDropdown.input) {
      // Editable quantity input — set value and trigger validation
      qtyDropdown.input.value = String(quantity);
      qtyDropdown.input.dispatchEvent(new Event('input', { bubbles: true }));
      console.log('[app-handoff-product] set editable qty input to:', quantity);
    }

    setTimeout(onDone, 150);
  }

  // ─── Inject _appHandoffId ─────────────────────────────────────────────────

  function injectAppHandoffId(variantSelects, ctx) {
    if (!variantSelects || !ctx.handoffId) return;

    // variantSelects.properties is the object that design-for-free.js reads
    // via its `properties` getter and passes into saveProductProperties()
    if (!variantSelects.properties) {
      variantSelects.properties = {};
    }

    variantSelects.properties._appHandoffId = ctx.handoffId;
    variantSelects.properties._appSource = 'arka_app_handoff';

    // If handoff has a custom 3D size, ensure it's stored in properties.
    // This acts as a safety net in case the size dropdown didn't fully propagate.
    var sizeOpt = ctx.selectedOptions && (ctx.selectedOptions.size || ctx.selectedOptions.Size);
    if (sizeOpt && isCustom3dSize(sizeOpt)) {
      variantSelects.properties._customSize = sizeOpt;
      variantSelects.properties.SizeLabel = sizeOpt;
      console.log('[app-handoff-product] injected custom size into properties:', sizeOpt);
    }

    console.log('[app-handoff-product] injected _appHandoffId:', ctx.handoffId);
    console.log('[app-handoff-product] current variantSelects.properties:', JSON.stringify(variantSelects.properties));
  }

  // ─── Auto-Start Design in Browser ─────────────────────────────────────────

  function autoStartDesignInBrowser(ctx) {
    var designBtn = document.querySelector('design-online');

    if (!designBtn) {
      showFallback('This product does not currently support Design in Browser. Please continue manually or contact support.');
      return;
    }

    // Check if button is hidden (e.g., material/validation restriction)
    if (designBtn.classList.contains('hidden')) {
      showFallback('Design in Browser is not available for the selected material. Please adjust your options or continue manually.');
      return;
    }

    // Mark as started to prevent re-trigger on back navigation
    try {
      localStorage.setItem(startedKey, 'true');
    } catch (e) {}

    console.log('[app-handoff-product] auto-clicking Design in Browser...');

    // Click the design-online element (its click listener calls getCustomizeUrl)
    var clickTarget = designBtn.querySelector('.custom-button') || designBtn;
    clickTarget.click();
  }

  // ─── Fallback Banner ──────────────────────────────────────────────────────

  function showFallback(message) {
    if (!container) return;
    container.innerHTML =
      '<div class="app-handoff-banner app-handoff-banner--expired">' +
        '<p class="app-handoff-banner__title">Action needed</p>' +
        '<p class="app-handoff-banner__body">' + escapeHtml(message) + '</p>' +
      '</div>';
  }

  // ─── Utilities ────────────────────────────────────────────────────────────

  function normalizeForComparison(str) {
    if (!str) return '';
    return String(str).trim().toLowerCase().replace(/\s+/g, '').replace(/[*×]/g, 'x');
  }

  function capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }
})();
